import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LockKey, Eye, EyeSlash } from 'phosphor-react';
import { toast } from 'sonner';

const Unlock = () => {
  const [passcode, setPasscode] = useState('');
  const [showPasscode, setShowPasscode] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const { unlock } = useAuth();
  const navigate = useNavigate();

  const handleUnlock = async (e) => {
    e.preventDefault();

    setIsUnlocking(true);
    try {
      await unlock(passcode);
      toast.success('Welcome back');
      navigate('/chat');
    } catch (error) {
      toast.error('Invalid passcode');
      setPasscode('');
    } finally {
      setIsUnlocking(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1759710465485-7cb5fe4c57cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHx3YXJtJTIwY296eSUyMHJvb20lMjBpbnRlcmlvciUyMHN1bmxpZ2h0fGVufDB8fHx8MTc3MTkzMDUzMHww&ixlib=rb-4.1.0&q=85)',
          filter: 'blur(8px)',
        }}
      />
      <div className="absolute inset-0 bg-stone-900/40" />

      <div className="relative max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/90 backdrop-blur-sm mb-6 shadow-glow">
            <LockKey size={40} weight="duotone" className="text-primary" />
          </div>
          <h1 className="text-5xl font-serif font-semibold text-white mb-2" data-testid="unlock-title">
            Welcome Back
          </h1>
          <p className="text-stone-200">
            Enter your passcode to unlock your sanctuary
          </p>
        </div>

        <div className="bg-white/95 backdrop-blur-md rounded-3xl p-8 shadow-soft">
          <form onSubmit={handleUnlock} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Passcode
              </label>
              <div className="relative">
                <input
                  type={showPasscode ? 'text' : 'password'}
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  className="w-full bg-stone-50 border-transparent focus:border-primary/50 focus:ring-2 focus:ring-primary/20 rounded-xl px-4 py-3 transition-all"
                  placeholder="Enter your passcode"
                  data-testid="unlock-passcode-input"
                  autoFocus
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPasscode(!showPasscode)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  data-testid="toggle-unlock-visibility"
                >
                  {showPasscode ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isUnlocking}
              className="w-full bg-primary text-white rounded-full px-8 py-4 font-medium hover:shadow-soft transition-all duration-300 active:scale-95 disabled:opacity-50"
              data-testid="unlock-btn"
            >
              {isUnlocking ? 'Unlocking...' : 'Unlock'}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-stone-500">
            <p>Forgot your passcode? Your data cannot be recovered.</p>
            <button
              onClick={() => navigate('/landing')}
              className="text-primary hover:underline mt-2"
              data-testid="back-to-home-link"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Unlock;